import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useUploadFiles} from 'helpers/hooks/utils';
import {H6, Span} from 'components/atoms/Text/Typography/Typography';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import StyledEntityHeading from 'components/molecules/Headings/EntityHeading/EntityHeading.styles';
import Box from 'components/atoms/Layout/Box/Box';
import Icon from 'components/atoms/Icons/Icon/Icon';
import IconButton from 'components/atoms/Buttons/IconButton/IconButton';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import Edit from '@mui/icons-material/Edit';
import ActionLogo from 'components/molecules/Images/ActionLogo/ActionLogo';
import ActionBadge from 'components/molecules/Badges/ActionBadge/ActionBadge';
import LogoDialog from 'components/organisms/Dialogs/LogoDialog/LogoDialog';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import HostnamesDialog from 'components/organisms/Dialogs/HostnamesDialog/HostnamesDialog';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import {createSvgIcon} from '@mui/material';
import utilsBasic from 'helpers/utils.basic';

const EntityHeading = React.forwardRef((props, ref) => {
  const {
    entity,
    showName,
    showLinks,
    showHostnames,
    isLoading,
    ActionLogoProps,
    ...innerProps
  } = useComponentProps({
    showName: true,
    showLinks: true,
    ...props
  }, 'EntityHeading', {
    static: ['isLoading', 'showLinks'],
    children: ['logo', 'name', 'links']
  });

  const profileProvider = useProfile();
  const [logoOpen, setLogoOpen] = useState(false);
  const [hostnamesOpen, setHostnamesOpen] = useState(false);

  const [uploadState, setUploadState, uploadFiles] = useUploadFiles();

  const updateEvent = useEffectEvent(profileProvider.updaters?.updateData);
  const createLogoEvent = useEffectEvent(profileProvider.updaters?.createLogo);

  const FileFieldProps = useMemo(() => {
    const fileState = uploadState ?
      Object.keys(uploadState).reduce((o, k) => {
        const split = k.split('_');
        o[split[1]] = uploadState[k];
        return o;
      }, {}) : null;

    return {
      FormFieldProps: {
        addDescription: false,
        status: fileState,
        minSize: 1,
        maxSize: constants.numbers.MB
      }
    }
  }, [uploadState]);

  const logoActionMemo = useMemo(() => ({
    auth: utils.createAuth({attribute: 'entity.update', meta: {entity}}),
    logo: entity?.logoUrl,
    altLogo: entity?.altLogoUrl,
    badge: <ActionBadge isLoading={isLoading}
                        action={{
                          label: <Icon icon={Edit} size="mini"/>,
                          auth: utils.createAuth({attribute: 'entity.update', meta: {entity}})
                        }}
                        hover={true}
                        size="smaller"
                        color="white" />,
    onClick: () => setLogoOpen(true),
    LogoProps: {
      outlined: true,
      fallbackIcon: HomeWorkOutlined,
      isLoading: isLoading
    }
  }), [entity, isLoading]);

  const handleLogoChange = () => {
    setUploadState(null);
  }

  const handleLogoSubmit = (logo) => {
    if (utils.isFile(logo)) {
      if (!utils.isDefined(logo.src) || logo.src !== entity?.logoUrl) {
        return createLogoEvent?.({
          entityId: entity?.entityId,
          filename: logo.name,
          description: logo.description,
          filesize: logo.size,
          filetype: logo.type || constants.filetypes.default
        })
          .then((res) => {
            const filename = res.response.data.data.filename;
            const uploadUrl = res.response.data.data.uploadUrl;
            const downloadUrl = res.response.data.data.downloadUrl;

            return uploadFiles([{file: logo, id: 'logo_0', url: uploadUrl, name: filename}], false)
              .then(() => {
                return updateEvent?.(entity, {logo_url: downloadUrl})
                  .then(() => {
                    setUploadState(null);
                  })
              })
          })
      }
    } else if (!logo) {
      return updateEvent?.(entity, {logo_url: ''})
        .then(() => {
          setUploadState(null);
        })
    }
  }

  const handleLogoClose = () => {
    setLogoOpen(false);
  }

  const renderLogoDialog = () => {
    if (logoOpen) {
      return <LogoDialog open={true}
                         name={entity?.name}
                         logo={(entity?.logoUrl !== entity?.altLogoUrl) ? entity?.logoUrl : null}
                         altLogo={entity?.altLogoUrl}
                         fallbackIcon={HomeWorkOutlined}
                         onChange={handleLogoChange}
                         onSubmit={handleLogoSubmit}
                         onClose={handleLogoClose}
                         FileFieldProps={FileFieldProps} />
    }
  }

  const hostnamesActionMemo = useMemo(() => {
    if (entity?.hostnames?.length > 1) {
      const activeHostnames = entity.hostnames.filter((h) => !h.excluded);
      const value = activeHostnames.length > 9 ? '9+' : activeHostnames.length.toString();
      const wide = value.length > 1;
      return {
        auth: utils.createAuth({attribute: 'entity.update', meta: {entity}}),
        tooltip: `${utils.formatNumber(activeHostnames.length)} of ${utils.formatNumber(entity.hostnames.length)} hostname${entity.hostnames.length === 1 ? '' : 's'} active`,
        onClick: () => setHostnamesOpen(true),
        icon: createSvgIcon(
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <circle r="9" cx="12" cy="12" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <text className="HostnamesIcon-text" x={wide ? '6' : '8.5'} y="16.45" fontSize="12">{value}</text>
          </svg>,
          'Hostnames'
        ),
        ActionIconButtonProps: {
          IconProps: {
            size: 'tiny'
          }
        },
        IconButtonProps: {
          isLoading: isLoading
        }
      }
    }
  }, [entity, isLoading]);

  const handleHostnamesSubmit = (hostnames) => {
    const originalExcluded = entity.hostnames
      .filter((h) => h.excluded)
      .map((h) => h.hostname);
    const excludedHostnames = entity.hostnames
      .filter((h) => !hostnames.find((hostname) => hostname === h.hostname))
      .map((h) => h.hostname);

    if (!utilsBasic.compare(excludedHostnames, originalExcluded)) {
      return updateEvent?.(entity, {excluded_hostnames: excludedHostnames});
    } else {
      return Promise.resolve();
    }
  }

  const handleHostnamesClose = () => {
    setHostnamesOpen(false);
  }

  const renderHostnamesDialog = () => {
    if (hostnamesOpen) {
      return <HostnamesDialog open={true}
                              hostnames={entity?.hostnames}
                              onSubmit={handleHostnamesSubmit}
                              onClose={handleHostnamesClose} />
    }
  }

  const renderActions = () => {
    if (showLinks || showHostnames) {
      if (isLoading) {
        return <Span isLoading={true} min={8} max={20}/>
      } else {
        const links = showLinks ? constants.data.links.map((link, idx) => {
          if (entity?.[link.value]?.length > 0) {
            return <Tooltip key={`${link.value}_${idx}`} showDisabled={true}
                            title={link.tooltip}
                            placement="bottom">
              <IconButton size="smaller"
                          density="densest"
                          variant="transparent"
                          onClick={() => utils.externalLink(entity?.[link.value])}>
                <Icon icon={link.icon} size="tiny"/>
              </IconButton>
            </Tooltip>
          } else {
            return null;
          }
        }).filter((_) => (_)) : null;
        const hostnames = (showHostnames && hostnamesActionMemo) ?
          <ActionIconButton size="smaller"
                            density="densest"
                            variant="transparent"
                            action={hostnamesActionMemo}/> : null;

        if (links || hostnames) {
          return <React.Fragment>
            {links}
            {hostnames}
          </React.Fragment>
        }
      }
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledEntityHeading ref={ref} {...innerProps}>
    <ActionLogo className="EntityHeading-logo"
                action={logoActionMemo}
                size="large"
                density="sparse"
                showInactive={true}
                {...ActionLogoProps} />
    <Box className="EntityHeading-info">
      {showName ? <H6 className="EntityHeading-name" min={8} max={20} isLoading={isLoading} showTooltip={true}>{entity?.name}</H6> : null}
      {(showLinks || showHostnames) ? <Box className="EntityHeading-actions">
        {renderActions()}
      </Box> : null}
    </Box>
    {renderLogoDialog()}
    {renderHostnamesDialog()}
  </StyledEntityHeading>
});

EntityHeading.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  entity: PropTypes.object,
  logoAction: PropTypes.object,
  showName: PropTypes.bool,
  showLinks: PropTypes.bool,
  showHostnames: PropTypes.bool,
  isLoading: PropTypes.bool,
  ActionLogoProps: PropTypes.object
};

export default EntityHeading;
